import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	signal,
	WritableSignal,
} from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OptionsBoardSettingsInterface } from '@shared/components/trading/widgets/options-board-widget/core/interfaces/options-board-settings.interface'
import { OptionsBoardHeaderComponent } from '@shared/components/trading/options-board/common/options-board-header/options-board-header.component'
import { catchError, finalize, forkJoin, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs'
import { OptionsFiltersInterface } from '@modules/options-module/general/interfaces/options-filters.interface'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { OptionsService } from '@modules/options-module/general/services/options.service'
import { OptionsResponseInterface } from '@modules/options-module/general/interfaces/options-response.interface'
import { OptionsBoardTableComponent } from '@shared/components/trading/options-board/common/options-board-table/options-board-table.component'
import { DirectionEnum } from '@shared/enums/direction.enum'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { OptionsHelperService } from '@modules/options-module/general/services/options-helper.service'
import { SocketPricesOptionsService } from '@modules/options-module/socket-prices/services/socket-prices-options.service'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { AlertService } from '@shared/services/alert.service'
import { TranslateService } from '@ngx-translate/core'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'

@UseLocales({ ru, en })
@Component({
	selector: 'app-options-board',
	templateUrl: './options-board.component.html',
	styleUrl: './options-board.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [OptionsBoardHeaderComponent, OptionsBoardTableComponent],
})
export class OptionsBoardComponent implements OnInit, OnDestroy {
	protected readonly directionEnum: typeof DirectionEnum = DirectionEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum

	public widget: InputSignal<WidgetInterface<OptionsBoardSettingsInterface> | null> =
		input<WidgetInterface<OptionsBoardSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _optionsService: OptionsService = inject<OptionsService>(OptionsService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _optionsHelperService: OptionsHelperService = inject<OptionsHelperService>(OptionsHelperService)
	private readonly _socketPricesOptionsService: SocketPricesOptionsService =
		inject<SocketPricesOptionsService>(SocketPricesOptionsService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)

	private _isOpenSocketOptions: boolean = false

	private _optionsFilters$: Subject<OptionsFiltersInterface> = new Subject<OptionsFiltersInterface>()
	private _topOptions: WritableSignal<OptionsResponseInterface | null> = signal<OptionsResponseInterface | null>(null)
	private _bottomOptions: WritableSignal<OptionsResponseInterface | null> = signal<OptionsResponseInterface | null>(
		null,
	)

	protected liveTicker: WritableSignal<LiveTickerInterface | null> = signal<LiveTickerInterface | null>(null)
	protected options: WritableSignal<OptionContractInterface[]> = signal<OptionContractInterface[]>([])
	protected isLoadingOptions: WritableSignal<boolean> = signal<boolean>(false)
	protected isLoadingTop: WritableSignal<boolean> = signal<boolean>(false)
	protected isLoadingBottom: WritableSignal<boolean> = signal<boolean>(false)
	protected isPoolScrollTrigger: WritableSignal<boolean> = signal<boolean>(false)

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	constructor() {
		effect((): void => {
			this._updateActiveOptionContract(this.options())
		})
	}

	private _getMergedOptions(): OptionContractInterface[] {
		const top: OptionsResponseInterface | null = this._topOptions()
		const bottom: OptionsResponseInterface | null = this._bottomOptions()
		const options: OptionContractInterface[] = [...(top?.results ?? []), ...(bottom?.results ?? [])]

		return options.sort(
			(a: OptionContractInterface, b: OptionContractInterface): number =>
				a.details.strike_price - b.details.strike_price,
		)
	}

	private _updateActiveOptionContract(options: OptionContractInterface[]): void {
		const activeOptionContract: OptionContractInterface | null =
			this._optionsHelperService.activeOptionContract$.getValue()
		if (!activeOptionContract) return
		const foundActiveOption: OptionContractInterface | undefined = options.find(
			(option: OptionContractInterface): boolean => option.details.ticker === activeOptionContract.details.ticker,
		)
		if (foundActiveOption) this._optionsHelperService.activeOptionContract$.next(foundActiveOption)
	}

	private _compareOptions(oldOptions: OptionContractInterface[], newOptions: OptionContractInterface[]): void {
		const oldTickers: string[] =
			oldOptions.map((option: OptionContractInterface): string => option.details.ticker) ?? []
		const newTickers: string[] =
			newOptions.map((option: OptionContractInterface): string => option.details.ticker) ?? []

		const addedTickers: string[] = newTickers.filter((ticker: string): boolean => !oldTickers.includes(ticker))
		const deletedTickers: string[] = oldTickers.filter((ticker: string): boolean => !newTickers.includes(ticker))

		if (addedTickers.length || deletedTickers.length) {
			this._openSocketOptions()
		}

		this._socketPricesOptionsService.toggleSubTickers({
			tickers: deletedTickers,
			action: 'unsubscribe',
			entity: this.constructor.name,
		})

		this._socketPricesOptionsService.toggleSubTickers({
			tickers: addedTickers,
			action: 'subscribe',
			entity: this.constructor.name,
		})
	}

	private _fillOptions(optionsSocketStocks: SocketStocksType[]): void {
		const marketStatus: MarketStatusEnum | null = this.marketStatus
		if (marketStatus !== this.marketStatusEnum.marketOpen) return
		const options: OptionContractInterface[] = this.options()

		const filledOption: OptionContractInterface[] = options.map(
			(option: OptionContractInterface): OptionContractInterface =>
				this._optionsHelperService.fillOption(option, optionsSocketStocks),
		)

		this.options.set(filledOption)
	}

	private _openSocketOptions(): void {
		if (!this._isOpenSocketOptions) {
			this._socketPricesOptionsService
				.openSocketOptions()
				.pipe(takeUntil(this._destroy$))
				.subscribe((response: SocketStocksType[]): void => {
					this._fillOptions(response)
				})

			this._isOpenSocketOptions = true
		}
	}

	private _listenResubscribeOptions(): void {
		this._socketPricesOptionsService.resubscribeOptions$.pipe(takeUntil(this._destroy$)).subscribe((): void => {
			const options: OptionContractInterface[] = this.options()
			const tickers: string[] = options.map((option: OptionContractInterface): string => option.details.ticker)
			this._socketPricesOptionsService.toggleSubTickers({
				tickers,
				action: 'subscribe',
				entity: this.constructor.name,
			})
		})
	}

	private _listenLiveTicker(): void {
		this._liveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: LiveTickerInterface | null): void => {
				this.liveTicker.set(response)
			})
	}

	private _listenOptionsFilters(): void {
		this._optionsFilters$
			.pipe(
				takeUntil(this._destroy$),
				switchMap(
					(
						filters: OptionsFiltersInterface,
					): Observable<[OptionsResponseInterface | null, OptionsResponseInterface | null]> => {
						return this._getOptions$(filters).pipe(
							catchError((): Observable<[null, null]> => {
								this._alertService.error(
									this._translateService.instant('optionsBoard.errors.couldNotGetOptions'),
								)
								return of([null, null])
							}),
						)
					},
				),
			)
			.subscribe(([top, bottom]: [OptionsResponseInterface | null, OptionsResponseInterface | null]): void => {
				const oldOptions: OptionContractInterface[] = this.options()
				this._topOptions.set(top)
				this._bottomOptions.set(bottom)
				this.isPoolScrollTrigger.update((value: boolean): boolean => !value)

				this.options.set(this._getMergedOptions())
				this._compareOptions(oldOptions, this.options())
			})
	}

	private _getOptions$(
		filters: OptionsFiltersInterface,
	): Observable<[OptionsResponseInterface | null, OptionsResponseInterface | null]> {
		const price: number | undefined = this.liveTicker()?.currentPrices?.price
		const limit: number = 50
		const sort: string = 'strike_price'

		return forkJoin([
			this._optionsService.getOptions({
				...filters,
				limit,
				sort,
				order: 'desc',
				'strike_price.lte': price,
			}),
			this._optionsService.getOptions({
				...filters,
				limit,
				sort,
				order: 'asc',
				'strike_price.gte': price,
			}),
		]).pipe(
			tap({
				subscribe: (): void => {
					this.isLoadingOptions.set(true)
					this._topOptions.set(null)
					this._bottomOptions.set(null)
				},
			}),
			finalize((): void => this.isLoadingOptions.set(false)),
		)
	}

	protected getOptionsByNextUrl(direction: DirectionEnum): void {
		let url: string | undefined = undefined

		switch (direction) {
			case this.directionEnum.TOP: {
				if (this.isLoadingTop()) break
				url = this._topOptions()?.next_url
				this.isLoadingTop.set(!!url)
				break
			}
			case this.directionEnum.BOTTOM: {
				if (this.isLoadingBottom()) break
				url = this._bottomOptions()?.next_url
				this.isLoadingBottom.set(!!url)
				break
			}
		}

		if (!url) return

		this._optionsService
			.getOptionsByNextUrl(url)
			.pipe(takeUntil(this._destroy$))
			.subscribe((response: OptionsResponseInterface): void => {
				switch (direction) {
					case this.directionEnum.TOP: {
						this._topOptions.update(
							(optionsResponse: OptionsResponseInterface | null): OptionsResponseInterface | null => {
								return {
									status: response.status,
									request_id: response.request_id,
									next_url: response.next_url,
									results: [...(optionsResponse?.results ?? []), ...response.results],
								}
							},
						)

						this.isLoadingTop.set(false)
						break
					}
					case this.directionEnum.BOTTOM: {
						this._bottomOptions.update(
							(optionsResponse: OptionsResponseInterface | null): OptionsResponseInterface | null => {
								return {
									status: response.status,
									request_id: response.request_id,
									next_url: response.next_url,
									results: [...(optionsResponse?.results ?? []), ...response.results],
								}
							},
						)
						this.isLoadingBottom.set(false)
						break
					}
				}

				const oldOptions: OptionContractInterface[] = this.options()
				this.options.set(this._getMergedOptions())
				this._compareOptions(oldOptions, this.options())
			})
	}

	protected onChangeFilters(filters: OptionsFiltersInterface): void {
		this._optionsFilters$.next(filters)
	}

	ngOnInit(): void {
		this._listenLiveTicker()
		this._listenOptionsFilters()
		this._listenResubscribeOptions()
	}

	ngOnDestroy(): void {
		this._socketPricesOptionsService.toggleSubTickers({
			destroyAll: true,
		})
		this._destroy$.next()
		this._destroy$.complete()
	}
}
